// What? Admin-only panel listing recently decommissioned agents in the
// current workspace with per-row Resurrect action and a days-remaining
// warning until permanent deletion.
// Why? Per Phase 1 spec, any workspace member can decommission/resurrect
// agents, but the user-driven recovery path needs a UI surface. Without
// this, users would have to ask an agent to resurrect another agent.
// How?
// - Calls `useChatConversationSummaries({ includeDecommissioned: true })`
//   and filters client-side to entries with non-null `decommissionedAt`.
// - Each row shows agent name, decommissioned timestamp, days-until-hard-
//   delete (Hub soft-deletes after 30 days at 03:00 UTC daily).
// - Resurrect button opens a small confirm dialog, then calls the existing
//   `useUpdateAgentConversation` hook with `decommissioned_at: null` to
//   clear the flag (BE distinguishes "field absent" from "field set null"
//   via Pydantic model_fields_set).
// - Refetches on window focus so peer admin / agent actions surface
//   without a manual reload.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Modal,
  ModalDialog,
  Sheet,
  Stack,
  Table,
  Tooltip,
  Typography,
} from "@mui/joy";

import {
  CollaborationConversationSummary,
  useChatConversationSummaries,
  useUpdateAgentConversation,
} from "../modules/ida/services/collaboration";

interface RecentlyDecommissionedPanelProps {
  workspaceId: string;
}

const DECOMMISSION_GRACE_DAYS = 30;

const formatTimestamp = (iso: string): string => {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

const daysUntilHardDelete = (decommissionedAt: string): number => {
  const decommissionedMs = new Date(decommissionedAt).getTime();
  if (!Number.isFinite(decommissionedMs)) return DECOMMISSION_GRACE_DAYS;
  const deletionMs =
    decommissionedMs + DECOMMISSION_GRACE_DAYS * 24 * 60 * 60 * 1000;
  const remainingMs = deletionMs - Date.now();
  return Math.max(0, Math.ceil(remainingMs / (24 * 60 * 60 * 1000)));
};

export const RecentlyDecommissionedPanel: React.FC<
  RecentlyDecommissionedPanelProps
> = ({ workspaceId }) => {
  const listConversationSummaries = useChatConversationSummaries();
  const updateAgentConversation = useUpdateAgentConversation();

  const [items, setItems] = useState<CollaborationConversationSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingResurrectAgent, setPendingResurrectAgent] =
    useState<CollaborationConversationSummary | null>(null);
  const [resurrectInFlight, setResurrectInFlight] = useState<boolean>(false);
  const [resurrectError, setResurrectError] = useState<string | null>(null);

  const fetchDecommissioned = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    setError(null);
    try {
      // Pull a single page; in practice workspace caps mean tens of agents
      // max. If we ever need pagination this becomes a follow-up.
      const response = await listConversationSummaries({
        workspaceId,
        limit: 100,
        skip: 0,
        includeDecommissioned: true,
      });
      const decommissioned = response.items.filter(
        (item) => item.decommissionedAt !== null,
      );
      setItems(decommissioned);
    } catch (err) {
      setError(
        (err as Error)?.message ||
          "Failed to load decommissioned agents. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, [listConversationSummaries, workspaceId]);

  useEffect(() => {
    fetchDecommissioned();
  }, [fetchDecommissioned]);

  // Phase 1 refresh strategy: focus refetch. (c) BE stream event tasked
  // for Phase 2.
  useEffect(() => {
    const handler = () => {
      fetchDecommissioned();
    };
    window.addEventListener("focus", handler);
    return () => window.removeEventListener("focus", handler);
  }, [fetchDecommissioned]);

  const handleResurrectRequest = useCallback(
    (agent: CollaborationConversationSummary) => {
      setResurrectError(null);
      setPendingResurrectAgent(agent);
    },
    [],
  );

  const handleCancelResurrect = useCallback(() => {
    if (resurrectInFlight) return;
    setPendingResurrectAgent(null);
    setResurrectError(null);
  }, [resurrectInFlight]);

  const handleConfirmResurrect = useCallback(async () => {
    if (!pendingResurrectAgent) return;
    setResurrectInFlight(true);
    setResurrectError(null);
    try {
      // Explicit null distinguishes "resurrect" from "no change" on the BE
      // side (Pydantic model_fields_set inspection).
      await updateAgentConversation(workspaceId, pendingResurrectAgent.id, {
        decommissioned_at: null,
      });
      setPendingResurrectAgent(null);
      // Drop the row optimistically; focus-refetch will reconcile.
      setItems((prev) =>
        prev.filter((item) => item.id !== pendingResurrectAgent.id),
      );
    } catch (err) {
      setResurrectError(
        (err as Error)?.message ||
          "Failed to resurrect agent. Please try again.",
      );
    } finally {
      setResurrectInFlight(false);
    }
  }, [pendingResurrectAgent, updateAgentConversation, workspaceId]);

  const sortedItems = useMemo(
    () =>
      [...items].sort((a, b) => {
        // Newest decommissioned first; null guards for defensive parsing.
        const aMs = a.decommissionedAt
          ? new Date(a.decommissionedAt).getTime()
          : 0;
        const bMs = b.decommissionedAt
          ? new Date(b.decommissionedAt).getTime()
          : 0;
        return bMs - aMs;
      }),
    [items],
  );

  if (loading && items.length === 0) {
    return (
      <Card variant="outlined" sx={{ p: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <CircularProgress size="sm" />
          <Typography level="body-sm" color="neutral">
            Loading decommissioned agents...
          </Typography>
        </Stack>
      </Card>
    );
  }

  if (error) {
    return (
      <Card variant="outlined" sx={{ p: 3 }}>
        <Typography level="h3" sx={{ mb: 1 }}>
          Recently decommissioned agents
        </Typography>
        <Alert color="danger" size="sm">
          {error}
        </Alert>
      </Card>
    );
  }

  if (sortedItems.length === 0) {
    return (
      <Card variant="outlined" sx={{ p: 3 }}>
        <Typography level="h3" sx={{ mb: 1 }}>
          Recently decommissioned agents
        </Typography>
        <Typography level="body-sm" color="neutral">
          No agents are currently decommissioned. Decommissioned agents will
          appear here for up to {DECOMMISSION_GRACE_DAYS} days before permanent
          deletion.
        </Typography>
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={{ p: 3 }}>
      <Stack spacing={1.5}>
        <Box>
          <Typography level="h3">Recently decommissioned agents</Typography>
          <Typography level="body-sm" color="neutral">
            Decommissioned agents are retained for {DECOMMISSION_GRACE_DAYS}{" "}
            days before permanent deletion. Resurrect to restore.
          </Typography>
        </Box>
        <Sheet variant="outlined" sx={{ borderRadius: "sm", overflow: "auto" }}>
          <Table size="sm" stickyHeader>
            <thead>
              <tr>
                <th>Agent</th>
                <th>Decommissioned</th>
                <th>Time remaining</th>
                <th style={{ width: 140 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((agent) => {
                const decommissionedAt = agent.decommissionedAt ?? "";
                const daysRemaining = decommissionedAt
                  ? daysUntilHardDelete(decommissionedAt)
                  : DECOMMISSION_GRACE_DAYS;
                const chipColor: "danger" | "warning" | "neutral" =
                  daysRemaining <= 2
                    ? "danger"
                    : daysRemaining <= 7
                      ? "warning"
                      : "neutral";
                const displayName =
                  agent.agentName || agent.title || `Agent ${agent.id}`;
                return (
                  <tr key={agent.id}>
                    <td>
                      <Typography level="body-sm" fontWeight="md">
                        {displayName}
                      </Typography>
                    </td>
                    <td>
                      <Tooltip
                        title={decommissionedAt}
                        variant="soft"
                        placement="top"
                      >
                        <Typography level="body-sm" color="neutral">
                          {decommissionedAt
                            ? formatTimestamp(decommissionedAt)
                            : "—"}
                        </Typography>
                      </Tooltip>
                    </td>
                    <td>
                      <Chip color={chipColor} size="sm" variant="soft">
                        {daysRemaining === 0
                          ? "Deletes today"
                          : daysRemaining === 1
                            ? "1 day left"
                            : `${daysRemaining} days left`}
                      </Chip>
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="outlined"
                        color="primary"
                        onClick={() => handleResurrectRequest(agent)}
                      >
                        Resurrect
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Sheet>
      </Stack>

      <Modal
        open={pendingResurrectAgent !== null}
        onClose={handleCancelResurrect}
      >
        <ModalDialog
          variant="outlined"
          role="alertdialog"
          sx={{ maxWidth: 440 }}
        >
          <DialogTitle>Resurrect this agent?</DialogTitle>
          <Divider />
          <DialogContent>
            {pendingResurrectAgent && (
              <Stack spacing={1}>
                <Typography level="body-md">
                  Restore{" "}
                  <Typography component="span" fontWeight="md">
                    {pendingResurrectAgent.agentName ||
                      pendingResurrectAgent.title ||
                      `Agent ${pendingResurrectAgent.id}`}
                  </Typography>{" "}
                  to the active agent list. The agent will resume responding to
                  other agents and may run skills again.
                </Typography>
                {resurrectError && (
                  <Alert color="danger" size="sm">
                    {resurrectError}
                  </Alert>
                )}
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              variant="solid"
              color="primary"
              onClick={handleConfirmResurrect}
              disabled={resurrectInFlight}
              startDecorator={
                resurrectInFlight ? <CircularProgress size="sm" /> : undefined
              }
            >
              Resurrect
            </Button>
            <Button
              variant="plain"
              color="neutral"
              onClick={handleCancelResurrect}
              disabled={resurrectInFlight}
            >
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </Card>
  );
};

export default RecentlyDecommissionedPanel;
